/**
 * Automações (Prompt 09): motor simples SE gatilho E condições ENTÃO ações.
 * Cada execução fica registrada; o mesmo acontecimento nunca dispara a mesma
 * regra duas vezes.
 */
import { z } from "zod";
import { ETAPAS_CONVERSA } from "./dominio";
import { STATUS_PEDIDO_CHAT } from "./erp";

export const GATILHOS = [
  "conversa_nova",
  "mensagem_recebida",
  "pedido_status",
  "pedido_parado",
  "sem_resposta_equipe",
  "cliente_sem_responder",
  "etapa_mudou",
] as const;
export type Gatilho = (typeof GATILHOS)[number];

export const ROTULO_GATILHO: Record<Gatilho, string> = {
  conversa_nova: "Cliente começa um atendimento novo",
  mensagem_recebida: "Cliente manda mensagem",
  pedido_status: "Pedido muda de status",
  pedido_parado: "Pedido parado no mesmo status",
  sem_resposta_equipe: "Cliente sem resposta da equipe",
  cliente_sem_responder: "Cliente não responde",
  etapa_mudou: "Conversa muda de etapa do funil",
};

const uuid = z.string().uuid();
const etapa = z.enum(ETAPAS_CONVERSA);
const statusPedido = z.enum(STATUS_PEDIDO_CHAT);

export const gatilhoSchema = z.discriminatedUnion("tipo", [
  z.object({ tipo: z.literal("conversa_nova") }),
  z.object({ tipo: z.literal("mensagem_recebida") }),
  z.object({ tipo: z.literal("pedido_status"), status: statusPedido }),
  z.object({
    tipo: z.literal("pedido_parado"),
    status: statusPedido,
    horas: z.number().int().min(1).max(720),
  }),
  z.object({ tipo: z.literal("sem_resposta_equipe"), minutos: z.number().int().min(1).max(1440) }),
  z.object({ tipo: z.literal("cliente_sem_responder"), horas: z.number().int().min(1).max(720) }),
  z.object({ tipo: z.literal("etapa_mudou"), etapa }),
]);
export type GatilhoAutomacao = z.infer<typeof gatilhoSchema>;

export const condicaoSchema = z.discriminatedUnion("tipo", [
  z.object({ tipo: z.literal("cliente_erp"), valor: z.boolean() }),
  z.object({ tipo: z.literal("compras_min"), valor: z.number().int().min(1).max(1000) }),
  z.object({ tipo: z.literal("ultima_compra_dias_min"), valor: z.number().int().min(1).max(3650) }),
  z.object({ tipo: z.literal("prioridade_min"), valor: z.number().int().min(1).max(2) }),
  z.object({ tipo: z.literal("tem_etiqueta"), etiquetaId: uuid }),
  z.object({ tipo: z.literal("sem_etiqueta"), etiquetaId: uuid }),
  z.object({ tipo: z.literal("etapa"), valor: etapa }),
  z.object({ tipo: z.literal("loja_aberta"), valor: z.boolean() }),
  z.object({ tipo: z.literal("sem_responsavel"), valor: z.boolean() }),
  /** Palavras separadas por vírgula; basta uma aparecer (sem acento, sem maiúscula). */
  z.object({ tipo: z.literal("texto_contem"), valor: z.string().trim().min(2).max(300) }),
]);
export type CondicaoAutomacao = z.infer<typeof condicaoSchema>;

export const ROTULO_CONDICAO: Record<CondicaoAutomacao["tipo"], string> = {
  cliente_erp: "Cliente cadastrada no ERP",
  compras_min: "Compras pagas, no mínimo",
  ultima_compra_dias_min: "Dias desde a última compra, no mínimo",
  prioridade_min: "Prioridade, no mínimo",
  tem_etiqueta: "Tem a etiqueta",
  sem_etiqueta: "Não tem a etiqueta",
  etapa: "Está na etapa",
  loja_aberta: "Loja aberta",
  sem_responsavel: "Sem vendedora responsável",
  texto_contem: "Mensagem contém alguma destas palavras",
};

const texto = z.string().trim().min(2).max(1000);

export const acaoSchema = z.discriminatedUnion("tipo", [
  z.object({ tipo: z.literal("etiqueta_conversa"), etiquetaId: uuid }),
  z.object({ tipo: z.literal("etiqueta_cliente"), etiquetaId: uuid }),
  z.object({ tipo: z.literal("remover_etiqueta"), etiquetaId: uuid }),
  z.object({ tipo: z.literal("prioridade"), valor: z.number().int().min(0).max(2) }),
  z.object({ tipo: z.literal("etapa"), valor: etapa }),
  z.object({ tipo: z.literal("avisar_responsavel"), texto }),
  z.object({ tipo: z.literal("alertar_gestao"), texto }),
  z.object({ tipo: z.literal("nota_interna"), texto }),
  /** Mensagem à cliente: só dentro da janela de 24h da Meta. */
  z.object({ tipo: z.literal("enviar_mensagem"), texto }),
  z.object({ tipo: z.literal("atribuir"), usuarioId: uuid.nullable() }),
  z.object({
    tipo: z.literal("acompanhar"),
    horas: z.number().int().min(1).max(720),
    texto: z.string().trim().max(300),
  }),
]);
export type AcaoAutomacao = z.infer<typeof acaoSchema>;

export const ROTULO_ACAO: Record<AcaoAutomacao["tipo"], string> = {
  etiqueta_conversa: "Pôr etiqueta na conversa",
  etiqueta_cliente: "Pôr etiqueta na cliente (vale para as próximas conversas)",
  remover_etiqueta: "Tirar etiqueta",
  prioridade: "Mudar prioridade",
  etapa: "Mudar etapa do funil",
  avisar_responsavel: "Avisar a vendedora responsável",
  alertar_gestao: "Alertar a gerência",
  nota_interna: "Escrever nota interna",
  enviar_mensagem: "Enviar mensagem à cliente",
  atribuir: "Passar para",
  acompanhar: "Marcar lembrete",
};

export const automacaoSchema = z.object({
  nome: z.string().trim().min(2).max(80),
  ativo: z.boolean().default(true),
  gatilho: gatilhoSchema,
  condicoes: z.array(condicaoSchema).max(10).default([]),
  acoes: z.array(acaoSchema).min(1, "Escolha ao menos uma ação.").max(10),
});
export type AutomacaoEntrada = z.infer<typeof automacaoSchema>;

export interface Automacao extends AutomacaoEntrada {
  id: string;
  execucoes7d: number;
  ultimaExecucaoEm: string | null;
  atualizadoEm: string;
}

export interface ExecucaoAutomacao {
  id: string;
  automacao: { id: string; nome: string };
  conversaId: string | null;
  contato: string | null;
  acoes: string[];
  erro: string | null;
  criadoEm: string;
}

/** Variáveis nos textos das ações. */
export const VARIAVEIS_AUTOMACAO = [
  "{nome}",
  "{vendedora}",
  "{pedido}",
  "{etapa}",
  "{compras}",
  "{ultima_compra}",
] as const;

export const PALETA_ETIQUETAS = [
  "#c69f70",
  "#8a6d3b",
  "#b4534b",
  "#c27c2c",
  "#4f8a5b",
  "#3f7a8c",
  "#5b5fa8",
  "#8a4f8a",
  "#6b6b6b",
] as const;

export const etiquetaSchema = z.object({
  nome: z.string().trim().min(2).max(40),
  cor: z.string().regex(/^#[0-9a-f]{6}$/i),
});
